import { commonFilter } from '../type';
import React, { useMemo, useState } from 'react';
import { Segment } from 'tea-component';
import { useChannelRank } from '../hooks';
import CardCommon from '@op/components/CardCommon';
import ChannelLabel from '@op/components/ChannelLabel';
import TableCommonHook from '@op/components/TableCommonHook';
import GameInfoItem from '@op/components/Snippets/GameInfoItem';
import PercentageItem from '@op/components/Snippets/PercentageItem';
import RingRatioBubble from '@op/components/Snippets/RingRatioBubble';
import RegionCountryItem from '@op/components/Snippets/RegionCountryItem';
import { DBO_TM, DBO_U, DBO_V } from '@op/utils/format';
import useLanguage from '@op/hooks/useLanguage';
import { sortable, SortBy } from 'tea-component/lib/table/addons';

interface ChannelRankProps {
  filter: commonFilter | null;
}

export default function ChannelRank({ filter }: ChannelRankProps) {
  const { data, activeTab, setActiveTab, loading } = useChannelRank({ filter });
  const { formatMessage } = useLanguage();
  return (
    <CardCommon
      hasTitleArrow
      handleTitleOnClick={() => {
        DBO_U.go({
          path: '/opinion/Overview/MarketPopularityRanking?tab=mobile',
          params: filter,
        });
      }}
      title={formatMessage("Weekly Tracking of Channel Ranking")}
      className="dbo-channel-rank-wrapper"
    >
      <Segment
        className={`card-slot-segment`}
        value={activeTab}
        onChange={(value) => setActiveTab(value)}
        options={[
          { text: formatMessage({ id: 'All' }), value: 'all' },
          ...['youtube', 'tiktok', 'instagram', 'twitter'].map((channel) => {
            return {
              text: (
                <ChannelLabel
                  iconStyle={{ width: 16 }}
                  channel_name={channel as 'youtube'}
                ></ChannelLabel>
              ),
              value: channel,
            };
          }),
        ]}
      />

      <ChannelRankTable
        data={data}
        loading={loading}
        filter={filter}
      ></ChannelRankTable>
    </CardCommon>
  );
}

function ChannelRankTable({ data, loading, filter }: any) {
  const { formatMessage } = useLanguage();
  const [sortBy, setSortBy] = useState<SortBy<string>>({
    order: 'desc',
    by: 'index',
  });
  const tableData = useMemo(() => {
    let current: any = null;
    const list = data.filter((it: any) => {
      if (it.edition_unified_id === filter.edition_unified_id) {
        current = it;
        return false;
      }
      return true;
    });
    const realSort: SortBy<string> =
      sortBy.by === 'index'
        ? { ...sortBy, order: sortBy.order === 'desc' ? 'asc' : 'desc' }
        : sortBy;

    list?.sort(sortable.comparer([realSort]));

    if(current){
      return [current, ...list];
    }

    return list;
  }, [data, sortBy]);



  const columns = useMemo(() => {
    return [
      {
        key: 'game',
        header: formatMessage('Game'),
        render: (cvm: any) => {
          return (
            <GameInfoItem item={cvm} showPlatform>
              {(borderClassName: string) => {
                return (
                  <span className={borderClassName}>
                    <RegionCountryItem
                      market={cvm.country}
                      text={cvm.publisher}
                      iconStyle={{ width: 16, marginRight: 4 }}
                      style={{ color: '#838697', fontSize: 12 }}
                    ></RegionCountryItem>
                  </span>
                );
              }}
            </GameInfoItem>
          );
        },
      },
      {
        key: 'index',
        header: formatMessage('Rank'),
        align: 'right',
        render: (cvm: any) => {
          return `#${cvm.index}`;
        },
      },
      {
        key: 'rankChange',
        header: formatMessage('Rank Change'),
        align: 'right',
        render: (cvm: any) => {
          return (
            <RingRatioBubble
              oldTime={cvm.last_week_time}
              newTime={cvm.newTime}
              oldValue={cvm.old_index}
              newValue={cvm.index}
              formateDate={DBO_TM.formatFunc('weekly')}
            >
              <PercentageItem
                formateFunc={(value) => Math.abs(value)}
                percentage={cvm.rankChange}
              ></PercentageItem>
            </RingRatioBubble>
          );
        },
      },
      {
        key: 'views',
        header: formatMessage('Views'),
        align: 'right',
        render: (cvm: any) => {
          return (
            <div>
              {DBO_V.reviews(cvm.views)}
              <RingRatioBubble
                oldTime={cvm.last_week_time}
                newTime={cvm.newTime}
                oldValue={cvm.views_old}
                newValue={cvm.views}
                formateDate={DBO_TM.formatFunc('weekly')}
                formateValue={(value) => DBO_V.reviews(value)}
              >
                <PercentageItem percentage={cvm.views_qoq}></PercentageItem>
              </RingRatioBubble>
            </div>
          );
        },
      },
      {
        key: 'engagement',
        header: formatMessage('Engagement'),
        align: 'right',
        render: (cvm: any) => {
          return (
            <div>
              {DBO_V.reviews(cvm.engagement)}
              <RingRatioBubble
                oldTime={cvm.last_week_time}
                newTime={cvm.newTime}
                oldValue={cvm.engagement_old}
                newValue={cvm.engagement}
                formateDate={DBO_TM.formatFunc('weekly')}
                formateValue={(value) => DBO_V.reviews(value)}
              >
                <PercentageItem
                  percentage={cvm.engagement_qoq}
                ></PercentageItem>
              </RingRatioBubble>
            </div>
          );
        },
      },
    ];
  }, [formatMessage]);

  return (
    <TableCommonHook
      bordered={'all'}
      minWidth={'auto'}
      columns={columns}
      records={tableData}
      hasPageable={false}
      loading={loading} 
      sortInfo={[sortBy, setSortBy]} 
      sortColumns={['index', 'rankChange', 'views', 'engagement']}
    ></TableCommonHook>
  );
}
